import { TableColumnConfigInterface } from '../../table.interface';

export class RowSorter<T> {
  private _config: Record<string, TableColumnConfigInterface<T>>;

  constructor(config: TableColumnConfigInterface<T>[] | Record<string, TableColumnConfigInterface<T>>) {
    this._config = Array.isArray(config)
      ? config.reduce((acc, column) => {
          acc[column.key as string] = column;
          return acc;
        }, {})
      : config;
  }

  public static readonly comparers = {
    boolean: (a, b) => (a ? -1 : 1),
    'boolean-sync': (a, b) => (a ? -1 : 1),
    switch: (a, b) => (a ? -1 : 1),
    text: (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
    default: RowSorter.defaultComparer,
  };

  private static defaultComparer(a, b) {
    // More verbose due to differences between chrome and firefox

    // Handle undefined values
    if (a === undefined && b === undefined) return 0;
    if (a === undefined) return 1;
    if (b === undefined) return -1;

    // Handle null values
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;

    // Compare values
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  public sort(data: T[], key: keyof T, direction: 'asc' | 'desc'): T[] {
    if (!key || direction === undefined) return data;

    const config = this._config[key as string];
    const comparer = this._getSortComparer(config);
    const directionMultiplier = direction === 'asc' ? 1 : -1;

    return data.sort((a, b) => comparer(a[key], b[key]) * directionMultiplier);
  }

  private _getSortComparer(column: TableColumnConfigInterface<T>): (a, b) => number {
    if (column?.sortComparator) {
      return (a, b) => column.sortComparator(a, b);
    }

    if (!column?.type) {
      return () => 0; // Don't sort if no config is found
    }

    if (RowSorter.comparers[column.type]) {
      return RowSorter.comparers[column.type];
    }

    return RowSorter.comparers['default'];
  }
}
